import { getCookie } from '@api/HelperAPI';
import {
    SEGMENTS,
    TDashboardSegment,
    TDashboardSegmentResponse
} from '@api/lendingAPI/dashboardSegmentResponse.types';
import { datadogLogs } from '@datadog/browser-logs';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';

const SEGMENT_OVERRIDE_KEY = 'dashboardSegment';

const OVERRIDE_HEADROOM = 25000;

const RESPONSE_DEFAULTS: Omit<TDashboardSegmentResponse, 'segment'> = {
    headroom: null,
    emphasis: 'standard',
    segment_entered_at: null
};

const NOT_SEGMENTED: TDashboardSegmentResponse = Object.freeze({
    ...RESPONSE_DEFAULTS,
    segment: null
});

const getSegmentOverride = (): TDashboardSegment | undefined => {
    const raw =
        getQueryParameter(getCurrentLocation(), SEGMENT_OVERRIDE_KEY) ??
        getCookie(SEGMENT_OVERRIDE_KEY);

    return SEGMENTS.find((segment) => segment === raw);
};

const fetchDashboardSegment = async (
    stateKey: string,
    accountId: string,
    override: TDashboardSegment | undefined
): Promise<TDashboardSegmentResponse> => {
    if (override) {
        return {
            segment: override,
            headroom: OVERRIDE_HEADROOM,
            emphasis: 'standard',
            segment_entered_at: null
        };
    }

    try {
        // TODO(TRT-2400): use a generated fetcher once the endpoint is
        // published in @iwoca/lapi-client.
        // eslint-disable-next-line iwoca/prefer-lapi-client
        const response = await fetch(
            `/api/lending/edge/dashboard_segment/${stateKey}/${accountId}/`
        );
        if (!response.ok) {
            // TODO(TRT-2400): drop the 404 exclusion once the endpoint ships,
            // after which a 404 is a real fault.
            if (response.status !== 404) {
                datadogLogs.logger.warn(
                    'dashboard_segment returned an error status',
                    { status: response.status }
                );
            }
            return NOT_SEGMENTED;
        }

        const body = await response.json();
        const data = body?.data as TDashboardSegmentResponse | undefined;

        const isRecognisedSegment =
            data !== undefined &&
            (data.segment === null || SEGMENTS.includes(data.segment));

        if (!isRecognisedSegment) {
            datadogLogs.logger.warn(
                'dashboard_segment returned an unrecognised segment',
                { segment: data?.segment ?? null }
            );
            return NOT_SEGMENTED;
        }

        return { ...RESPONSE_DEFAULTS, ...data };
    } catch (error) {
        datadogLogs.logger.warn('dashboard_segment request threw', {
            error: error instanceof Error ? error.message : error
        });
        return NOT_SEGMENTED;
    }
};

export const getDashboardSegmentQueryConfig = (
    stateKey: string,
    accountId: string
) => {
    const override = getSegmentOverride();

    return {
        queryKey: ['dashboardSegment', stateKey, accountId, override],
        queryFn: () => fetchDashboardSegment(stateKey, accountId, override)
    };
};
